import { Grid, Stack } from "@mui/material";
import Form from "components/form/Form";
import useSetTabTitle from "hooks/useSetTabTitle";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import Header from "./components/Header";
import DocumentsBlock from "./components/form/DocumentsBlock";
import EvaluationTypeBlock from "./components/form/EvaluationTypeBlock";
import GradesBlock from "./components/form/GradesBlock";
import InfoBlock from "./components/form/InfoBlock";
import InstructionsBlock from "./components/form/InstructionsBlock";
import QuestionTypesBlock from "./components/form/QuestionTypesBlock";
import TitleBlock from "./components/form/TitleBlock";
import { PROMPT_SLIDES, QUESTION_TYPE_ERROR_MESSAGE, QUESTION_TYPE_OPTIONS } from "./constants";
import { CompetencyMapGenerationFormValues, PromptSlideId } from "./types";
import {
  calculateQuestionMetrics,
  getDefaultGenerationFormValues,
  getDifficultyGradeLabel,
  getQuestionTypeCount,
} from "./utils";

const CompetencyMapGenerationPage = () => {
  const [activeSlideId, setActiveSlideId] = useState<PromptSlideId>(PROMPT_SLIDES[0].id);
  const [skippedPromptIds, setSkippedPromptIds] = useState<PromptSlideId[]>([]);
  const form = useForm<CompetencyMapGenerationFormValues>({
    defaultValues: getDefaultGenerationFormValues(),
    mode: "onChange",
  });
  const { control, setValue, getValues, setError, clearErrors, formState } = form;

  const difficulty = useWatch({ control, name: "difficulty" }) || 5;
  const language = useWatch({ control, name: "language" });
  const questionTypeCounts = useWatch({ control, name: "questionTypeCounts" }) || getValues("questionTypeCounts");

  useSetTabTitle("Генерация карты компетенций из документов");

  const questionMetrics = useMemo(
    () => calculateQuestionMetrics(questionTypeCounts, difficulty),
    [difficulty, questionTypeCounts]
  );
  const difficultyGradeLabel = useMemo(() => getDifficultyGradeLabel(difficulty), [difficulty]);

  useEffect(() => {
    const currentDuration = getValues("durationMinutes");

    if (currentDuration !== questionMetrics.estimatedDuration) {
      setValue("durationMinutes", questionMetrics.estimatedDuration, { shouldDirty: false, shouldValidate: false });
    }

    if (Object.values(questionTypeCounts).some((count) => count > 0)) {
      clearErrors("questionTypeCounts");
    }
  }, [clearErrors, getValues, questionMetrics.estimatedDuration, questionTypeCounts, setValue]);

  const handleSetQuestionTypeCount = (
    questionTypeId: (typeof QUESTION_TYPE_OPTIONS)[number]["id"],
    nextCount: number
  ) => {
    const safeCount = Math.max(0, nextCount);
    const nextQuestionTypeCounts = {
      ...getValues("questionTypeCounts"),
      [questionTypeId]: safeCount,
    };

    setValue("questionTypeCounts", nextQuestionTypeCounts, { shouldDirty: true, shouldValidate: true });

    if (Object.values(nextQuestionTypeCounts).some((count) => count > 0)) {
      clearErrors("questionTypeCounts");
      return;
    }

    if (formState.isSubmitted) {
      setError("questionTypeCounts", { type: "manual", message: QUESTION_TYPE_ERROR_MESSAGE });
    }
  };

  const handleToggleQuestionType = (questionTypeId: (typeof QUESTION_TYPE_OPTIONS)[number]["id"], checked: boolean) => {
    const currentCount = getQuestionTypeCount(getValues("questionTypeCounts"), questionTypeId);

    handleSetQuestionTypeCount(questionTypeId, checked ? Math.max(currentCount, 1) : 0);
  };

  const handleAdjustQuestionType = (questionTypeId: (typeof QUESTION_TYPE_OPTIONS)[number]["id"], delta: number) => {
    const currentCount = getQuestionTypeCount(getValues("questionTypeCounts"), questionTypeId);

    handleSetQuestionTypeCount(questionTypeId, currentCount + delta);
  };

  const handleChangeLanguage = (nextLanguage: CompetencyMapGenerationFormValues["language"]) => {
    setValue("language", nextLanguage, { shouldDirty: true });
  };

  return (
    <Form form={form}>
      <Stack spacing={2.5}>
        <Header
          setActiveSlideId={setActiveSlideId}
          setSkippedPromptIds={setSkippedPromptIds}
        />
        <TitleBlock />
        <Grid
          container
          gap={1}
          alignItems='stretch'
          sx={{ flexWrap: { xs: "wrap", lg: "nowrap" } }}>
          <Grid
            item
            xs={12}
            lg={2.5}>
            <Stack
              spacing={1}
              sx={{ height: "100%" }}>
              <Stack
                sx={{
                  flex: 1,
                  minHeight: 0,
                  "& > *": {
                    height: "100%",
                  },
                }}>
                <DocumentsBlock />
              </Stack>
              <GradesBlock difficultyGradeLabel={difficultyGradeLabel} />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            lg={7}>
            <Stack spacing={1}>
              <Grid
                container
                gap={1}
                sx={{ flexWrap: { xs: "wrap", md: "nowrap" } }}>
                <Grid
                  item
                  xs={12}
                  md={6}>
                  <EvaluationTypeBlock
                    evaluationType={questionMetrics.evaluationType}
                    estimatedDuration={questionMetrics.estimatedDuration}
                    indicatorPercent={questionMetrics.indicatorPercent}
                    language={language}
                    onChangeLanguage={handleChangeLanguage}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}>
                  <QuestionTypesBlock
                    questionTypeCounts={questionTypeCounts}
                    totalActiveTypes={questionMetrics.totalActiveTypes}
                    errorMessage={formState.errors.questionTypeCounts?.message}
                    onToggleQuestionType={handleToggleQuestionType}
                    onAdjustQuestionType={handleAdjustQuestionType}
                  />
                </Grid>
              </Grid>
              <InstructionsBlock
                activeSlideId={activeSlideId}
                skippedPromptIds={skippedPromptIds}
                setActiveSlideId={setActiveSlideId}
                setSkippedPromptIds={setSkippedPromptIds}
              />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            lg={2.5}>
            <InfoBlock
              questionMetrics={questionMetrics}
              difficultyGradeLabel={difficultyGradeLabel}
              skippedPromptIds={skippedPromptIds}
            />
          </Grid>
        </Grid>
      </Stack>
    </Form>
  );
};

export default CompetencyMapGenerationPage;
